import * as React from 'react';
import { createContext, useState, useEffect } from 'react';
import pluralize from 'pluralize';
import { SearchBar } from './SearchBar';
import PropTypes from 'prop-types';
import Progress from '../../shared/Progress';
import SearchTable from './SearchTable';
import { batch, useDispatch, useSelector } from 'react-redux';
import { calendarActiveSearch, getRecentlyViewed } from '../../../../api/emailsApi/calendar';
import { changeAlertText } from '../../../actions/index';
import { getLocalStorageWithExpiry, setLocalStorageWithExpiry, THIRTY_MINUTES_INT } from '../../../utils/browser';
import Svg from '../../shared/Svg';
import './SearchContainer.scss';
import { isSelectingRecord } from '../../../actions/calendar';
import colors from '../../../utils/colors';

export const SearchContext = createContext({});
const { capitalCase } = require('change-case');

const debounce = (cb, delay) => () => {
  const timeout = setTimeout(cb, delay);
  return () => clearTimeout(timeout);
};

/**
 * SearchContainer is a multi use component that holds most of the logic around presenting records
 * and performing searches.
 * @return {function}
 */
export const SearchContainer = ({ showBack }) => {
  const dispatch = useDispatch();
  const [searchResultsLoading, changeSearchResultsLoadingState] = useState(false);
  const objectDefaults = useSelector((state) => state.mail.settings?.objectPickerDefaults);
  const salesforceProfileId = useSelector((state) => state.auth.user?.salesforceProfileId);

  const getDefaultSearchObject = (state) => {
    const objectsAndDisplayFields = state.mail.settings?.objectsAndDisplayFields;
    const defaultObject = salesforceProfileId && objectDefaults && objectDefaults[salesforceProfileId];
    if (defaultObject) {
      const filteredDefaultObjects = objectsAndDisplayFields.filter((object) => object.apiName === defaultObject.apiName);
      return filteredDefaultObjects[0];
    } else {
      return objectsAndDisplayFields[0];
    }
  };

  const onBackClicked = () => {
    dispatch(isSelectingRecord(false));
  };

  const formatPassiveSearch = (recentlyViewedRecords, listViewRecords, viewName) => {
    const newRecords = {};
    newRecords['Recently Viewed'] = recentlyViewedRecords;
    if (listViewRecords?.length && viewName) {
      newRecords[viewName] = listViewRecords;
    }
    return newRecords;
  };

  // Track the current and prior search object to detect a change.
  const defaultSearchObject = useSelector((state) => getDefaultSearchObject(state));
  const recentRecords = useSelector((state) => state.calendar.recentRecords);
  const listView = useSelector((state) => state.calendar.listViews);
  const [searchObject, updateSearchObject] = useState(defaultSearchObject);
  const [records, updateSearchResults] = useState();
  const [searchQuery, updateSearchQuery] = useState('');

  const updateSearchObjectCallback = (apiName) => {
    updateSearchObject(apiName);
    updateSearchQuery('');
  };


  const validateResponse = (response, source) => {
    if (response instanceof Error) {
      const message = source === 'search' ? 'Error searching. Please retry.' : 'Error loading';
      updateSearchObjectCallback(defaultSearchObject);
      batch(() => {
        dispatch(changeAlertText(response.response?.data?.Message || response.data?.Message || message, 'error'));
      });
      return formatPassiveSearch(recentRecords, listView, defaultSearchObject?.searchView?.Name);
    }
  };

  const updateRecordsTable = async () => {
    changeSearchResultsLoadingState(true);

    let newRecords = {};
    // If there is a query, perform an active search
    if (searchQuery) {
      const response = await calendarActiveSearch(searchObject.apiName, searchQuery);
      const defaultRecords = validateResponse(response, 'search');
      if (defaultRecords) {
        newRecords = defaultRecords;
      } else {
        const defaultLabel = `My ${searchObject.pluralLabel}`;
        // If no list view selected, searchView will not be set. If set to "None", it will be an empty object
        if (response.data?.ListViewRecords?.length && searchObject?.searchView && Object.keys(searchObject.searchView).length > 0) {
          const listViewTabName = searchObject.searchView?.Name || defaultLabel;
          newRecords[listViewTabName] = response.data.ListViewRecords;
        } else if (response.data?.ListViewRecords?.length) {
          newRecords[defaultLabel] = response.data.ListViewRecords;
        }

        const allRecordsTabName = `All ${pluralize(capitalCase(searchObject.label))}`;
        newRecords[allRecordsTabName] = response.data?.AllRecords;
      }
    } else {
      // if there is no query, we are not using the default object, use passive search for new object
      let response = {};
      const cacheKey = `litifyobj#${searchObject.apiName}`;
      const cachedObjectDetails = getLocalStorageWithExpiry(cacheKey);
      if (!cachedObjectDetails) {
        response = await getRecentlyViewed(searchObject.apiName);
        setLocalStorageWithExpiry(cacheKey, response.data, THIRTY_MINUTES_INT);
      } else {
        response.data = cachedObjectDetails; // set response.data from cache
      }
      newRecords = validateResponse(response) || formatPassiveSearch(response.data?.AllRecords, response.data?.ListViewRecords, searchObject?.searchView?.Name);
    }

    updateSearchResults(newRecords);
    changeSearchResultsLoadingState(false);
  };

  useEffect(() => {
    updateRecordsTable();
  }, [searchObject, recentRecords]);

  const debounceEffect = () => {
    if (![1, 2].includes(searchQuery.length)) {
      updateRecordsTable();
    }
  };

  useEffect(debounce(() => {
    debounceEffect();
  }, 700), [searchQuery]);

  useEffect(() => {
    if (![1, 2].includes(searchQuery.length)) {
      changeSearchResultsLoadingState(true);
    }
  }, [searchQuery]);

  // If there is a search query, generate multiple tabs determined by header texts.
  return (
    <div id='search-container'>
      { showBack && <div className='search-container-back-container'>
        <button // back button out of line on left side compared to mock
          className='search-container-back'
          onClick={onBackClicked}
        >
          <Svg
            className='search-container-back-icon'
            data='/assets/back.svg'
            fill={colors.strongBlue}
            onClick={onBackClicked}
          />
        Back
        </button>
      </div>}
      <SearchContext.Provider value={{
        searchObject,
        searchQuery,
        searchObjectUpdateCallback: updateSearchObjectCallback,
        searchQueryCallback: updateSearchQuery,
      }} >
        <SearchBar />
        { searchResultsLoading && <Progress /> }
        <SearchTable
          tableInfo={records}
          isSearching={searchResultsLoading}
        />
      </SearchContext.Provider>
    </div>
  );
};

SearchContainer.propTypes = {
  showBack: PropTypes.bool,
};
